import { useState, type FormEvent } from 'react';
import { submitInquiry } from '../../lib/api';
import type { InquiryInput } from '../../lib/types';
import { inquiryTypes } from '../../content/siteContent';

declare global {
  interface Window {
    turnstile?: { reset: () => void };
  }
}

function initialInquiry(): InquiryInput {
  const params = new URLSearchParams(window.location.search);
  const presetType = params.get('type') ?? '';
  return {
    name: '', email: '', phone: '', message: '', website: '', turnstileToken: '',
    inquiryType: (inquiryTypes as readonly string[]).includes(presetType) ? presetType : inquiryTypes[0],
    itemRef: (params.get('item') ?? '').replace(/[^a-z0-9-]/g, '')
  };
}

function itemLabel(slug: string) {
  return slug.split('-').filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function requiredFields(values: InquiryInput) {
  return !values.name.trim() || !values.email.trim() || !values.message.trim();
}

export function InquiryForm({ turnstileToken = '' }: { turnstileToken?: string }) {
  const [values, setValues] = useState({ ...initialInquiry(), turnstileToken });
  const [status, setStatus] = useState<'idle' | 'pending' | 'sent'>('idle');
  const [error, setError] = useState('');

  function update(field: keyof InquiryInput, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    if (requiredFields(values)) {
      setError('Please complete the required fields.');
      return;
    }

    setStatus('pending');
    try {
      const widgetToken = new FormData(event.currentTarget).get('cf-turnstile-response');
      await submitInquiry({
        ...values,
        turnstileToken: values.turnstileToken || (typeof widgetToken === 'string' ? widgetToken : '')
      });
      setStatus('sent');
      setValues({ ...initialInquiry(), turnstileToken });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to send your inquiry.');
      window.turnstile?.reset();
      setStatus('idle');
    }
  }

  if (status === 'sent') {
    return <div className="inquiry-confirmation" role="status"><h2>Thank you.</h2><p>Your inquiry has been saved. Our team will follow up soon.</p></div>;
  }

  return (
    <form className="inquiry-form" noValidate onSubmit={handleSubmit}>
      <div className="inquiry-form__heading">
        <p className="eyebrow">Get in touch</p>
        <h2>Send us a message.</h2>
      </div>
      {error && <div className="form-error" id="inquiry-error" role="alert">{error}</div>}
      <div className="form-grid">
        <label>Name<input name="name" autoComplete="name" required value={values.name} onChange={(event) => update('name', event.target.value)} /></label>
        <label>Email<input name="email" type="email" autoComplete="email" required value={values.email} onChange={(event) => update('email', event.target.value)} /></label>
        <label>Phone (optional)<input name="phone" type="tel" autoComplete="tel" value={values.phone} onChange={(event) => update('phone', event.target.value)} /></label>
        <label>What is this about?<select name="inquiryType" value={values.inquiryType} onChange={(event) => update('inquiryType', event.target.value)}>
          {inquiryTypes.map((type) => <option key={type}>{type}</option>)}
        </select></label>
        {values.itemRef && <p className="inquiry-item">About: {itemLabel(values.itemRef)}</p>}
        <label className="form-grid__wide">Message<textarea name="message" rows={6} required value={values.message} onChange={(event) => update('message', event.target.value)} /></label>
      </div>
      <label className="honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={values.website} onChange={(event) => update('website', event.target.value)} /></label>
      {!turnstileToken && <div className="cf-turnstile" data-sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY} aria-label="Security check" />}
      <button className="button" disabled={status === 'pending'} type="submit" aria-describedby={error ? 'inquiry-error' : undefined}>
        {status === 'pending' ? 'Sending…' : 'Send inquiry'}
      </button>
    </form>
  );
}
